"use client";

import { type RefObject, useLayoutEffect } from "react";

/**
 * Full-screen overlays (sheets, dialogs, the chat) must NOT be
 * `position: fixed` — use this hook to place them with `position: absolute`
 * over the part of the page that is on screen.
 *
 * ## The Safari bug this works around
 *
 * Safari on iOS 26+ ("Liquid Glass") draws its floating bottom toolbar over
 * the page, and paints the strip under that toolbar with a colour it samples
 * from the page. A `position: fixed` element touching the screen edge wins
 * that sampling — and Safari keeps the sampled colour after the element is
 * removed. So opening and closing any white full-screen sheet left a solid
 * white band under the toolbar, cutting the page off above it, until reload.
 * Scrolling does not clear it.
 *
 * JavaScript cannot see the band: `innerHeight`, `visualViewport` (height and
 * offsetTop), `100svh`/`100dvh`/`100lvh`/`100vh`, `env(safe-area-inset-*)` and
 * `documentElement.clientHeight` are identical before and after (measured on
 * an iPhone, Safari "Version/27.0", 714 / 754px). It is browser chrome.
 *
 * Experiments on the device, each on a freshly reloaded page (a fresh page
 * has no band), showing a full-screen layer for 1.5s and removing it:
 *
 * | Layer                                                    | Band after? |
 * | -------------------------------------------------------- | ----------- |
 * | `body { overflow: hidden }` alone, no layer              | no          |
 * | `fixed; inset: 0; background: red`                       | red band    |
 * | same, colour on an inner `absolute; inset: 0` child      | red band    |
 * | same, `rgba(255, 0, 0, 0.99)` (compositor path)          | red band    |
 * | red fixed layer, then an empty transparent fixed one     | red band    |
 * | `absolute`, top = scrollY, height = innerHeight, red     | **no band** |
 *
 * Fixes suggested online for iOS 26 (`viewport-fit=cover`, a background on
 * `html`, `display: none` instead of `opacity: 0`, translucent backgrounds)
 * did not help on this Safari; only not being `fixed` does.
 *
 * ## How to use it
 *
 * 1. Portal the overlay to `document.body`, so the page (initial containing
 *    block) — not a positioned or transformed ancestor — positions it.
 * 2. Give it `absolute inset-x-0 top-0 h-dvh` (the classes are only the
 *    first-paint fallback; this hook sets `top`/`height` inline before paint).
 * 3. Call `usePageOverlay(ref, open)`. While enabled it pins the overlay to
 *    the scroll offset at open time and the layout-viewport height, and marks
 *    it `data-page-overlay`, which locks page scroll (globals.css) so the
 *    page can't scroll away from under it.
 * 4. Responsive overlays that are full-screen only on phones pass
 *    `{ media: BELOW_SM }` and switch back with `sm:fixed …`: above
 *    the breakpoint the hook clears its inline styles and leaves them alone.
 *
 * ## The strip under the toolbar
 *
 * Safari's floating toolbar sits *below* the layout viewport, over page
 * content (`100lvh` is 754px vs `innerHeight` 714px on the test iPhone). A
 * fixed overlay got painted there by Safari; an absolute one of
 * `innerHeight` ends where the toolbar starts, and the page showed through
 * under the toolbar. So the overlay also gets a `box-shadow` that is a copy
 * of itself shifted down by its own height: it paints the strip in the
 * overlay's colour but, unlike a taller box, adds no layout or scroll height
 * and doesn't push the overlay's own content (bottom buttons, the chat
 * composer) under the toolbar. The colour is the overlay's computed
 * background, or `bleed` — e.g. a full-screen-on-phones dialog whose own
 * background is the translucent scrim passes `var(--surface)`.
 *
 * Small popovers (menus, pickers) can stay `fixed`: they don't touch the
 * bottom edge, so Safari doesn't sample them.
 */
/**
 * Below Tailwind's `sm` breakpoint. Deliberately not the range syntax
 * `(width < 40rem)`: Safari before 16.4 can't parse it, `matchMedia` then
 * never matches, and the sheet stays at the top of the page — off screen once
 * the page is scrolled. (CSS is downlevelled by the build; a JS string isn't.)
 */
export const BELOW_SM = "not all and (min-width: 40rem)";

export function usePageOverlay(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
  { media, bleed }: { media?: string; bleed?: string } = {}
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const mql = media ? window.matchMedia(media) : null;
    // Pinned to where the page was when the overlay opened; the page is
    // scroll-locked meanwhile, so that stays the visible part.
    const top = window.scrollY;
    const clear = () => {
      el.style.top = "";
      el.style.height = "";
      el.style.boxShadow = "";
      delete el.dataset.pageOverlay;
    };
    const place = () => {
      if (mql && !mql.matches) return clear();
      el.style.top = `${top}px`;
      // The layout viewport. On iOS it doesn't shrink for the keyboard, so
      // the overlay keeps covering the page behind it (keyboard handling for
      // content inside is `useVisualViewportFit`'s job).
      el.style.height = `${window.innerHeight}px`;
      // Continue the overlay's colour under Safari's toolbar (see above).
      el.style.boxShadow = "";
      const color = bleed ?? getComputedStyle(el).backgroundColor;
      if (color && color !== "rgba(0, 0, 0, 0)" && color !== "transparent") {
        el.style.boxShadow = `0 ${window.innerHeight}px 0 0 ${color}`;
      }
      el.dataset.pageOverlay = "";
    };
    place();
    window.addEventListener("resize", place);
    mql?.addEventListener("change", place);
    return () => {
      window.removeEventListener("resize", place);
      mql?.removeEventListener("change", place);
      clear();
    };
  }, [ref, enabled, media, bleed]);
}
